// ============================================================================
// Dashboard widget registry — single source of truth for every widget the
// dashboard can render: stable widget_key, display name, description, role
// gate, grid size, default visibility, and which data domains it needs from
// the shared dashboard hook.
//
// The FINANCE/HR/INCENTIVE role Sets used to live as scattered consts in
// dashboard.tsx — they're lifted here so the tailor panel, the grid renderer,
// and the prefs resolver all agree on who may see what. RLS remains the hard
// boundary; these gates are presentation-level on top of it.
//
// widget_key values are persisted in user_dashboard_prefs.layout — NEVER
// rename one without migrating stored prefs.
// ============================================================================
import { DESK_SEATS, FINANCE_PAGES, INCENTIVE_SEATS } from '../../shared/role-gates';


export type DataDomain =
  | 'facilities'
  | 'accounts'
  | 'opportunities'
  | 'feedback'
  | 'assignments'
  | 'hiring';

// Grid sizing: small = 1/3 row on xl, medium = 1/2 row on xl, full = full row.
export type WidgetSize = 'small' | 'medium' | 'full';

export interface WidgetDef {
  key: string;
  name: string;
  description: string;
  /** 'all' = any authenticated dashboard viewer; otherwise the allowed role set. */
  roles: 'all' | ReadonlySet<string>;
  size: WidgetSize;
  /** Whether the widget is shown when the user has no saved prefs (default layout). */
  defaultVisible: boolean;
  /**
   * Shared-hook query domains the widget consumes. Used so hiding widgets
   * actually skips the underlying queries. Self-fetching widgets list none —
   * they gate their own fetch via `enabled` / mount.
   */
  domains: readonly DataDomain[];
  /** True when the widget fetches its own data rather than via useDashboardData. */
  selfFetching?: boolean;
}

export interface LayoutEntry {
  widget_key: string;
  visible: boolean;
}

// ----- Role gates (formerly scattered Sets in dashboard.tsx) -----------------

export const FINANCE_INBOX_ROLES: ReadonlySet<string> = new Set<string>(FINANCE_PAGES);

export const HR_INBOX_ROLES: ReadonlySet<string> = new Set<string>(DESK_SEATS);

// Incentive payouts card — same money-visibility set as the module's RLS.
export const INCENTIVE_ROLES: ReadonlySet<string> = new Set<string>(INCENTIVE_SEATS);

// ----- Registry ---------------------------------------------------------------
// Registry order == default layout order == exactly today's dashboard, top to
// bottom: inbox row, invoicing, hiring KPIs, incentive queue, portfolio KPI
// row, margin pair, then the six core scan/drill widgets.

export const WIDGET_REGISTRY: readonly WidgetDef[] = [
  {
    key: 'finance_inbox',
    name: 'Finance inbox',
    description:
      'Finance-actionable counts — invoicing queue, open finance tasks, rate proposals, expired COIs.',
    roles: FINANCE_INBOX_ROLES,
    size: 'medium',
    defaultVisible: true,
    domains: [],
    selfFetching: true,
  },
  {
    key: 'hr_inbox',
    name: 'HR inbox',
    description: 'HR-actionable counts — hiring, onboarding, and people tasks.',
    roles: HR_INBOX_ROLES,
    size: 'medium',
    defaultVisible: true,
    domains: [],
    selfFetching: true,
  },
  {
    key: 'invoicing',
    name: 'Invoicing',
    description: 'Unbilled hours and open invoice drafts (count + $).',
    roles: FINANCE_INBOX_ROLES,
    size: 'medium',
    defaultVisible: true,
    domains: [],
    selfFetching: true,
  },
  {
    key: 'hiring_kpis',
    name: 'Hiring KPIs',
    description: 'Open reqs, filled this month, and average days to fill.',
    roles: HR_INBOX_ROLES,
    size: 'full',
    defaultVisible: true,
    domains: [],
    selfFetching: true,
  },
  {
    key: 'incentive_queue',
    name: 'Performance payments',
    description: 'Performance-payment periods needing action, linked into the Performance payments module.',
    roles: INCENTIVE_ROLES,
    size: 'full',
    defaultVisible: true,
    domains: [],
    selfFetching: true,
  },
  {
    key: 'kicker_queue',
    name: 'Annual increases',
    description:
      'Upcoming inflationary kickers in 30/60/90 buckets plus seeded dates needing review — links into the Annual Increases queue.',
    roles: FINANCE_INBOX_ROLES,
    size: 'medium',
    defaultVisible: true,
    domains: [],
    selfFetching: true,
  },
  {
    key: 'new_accounts',
    name: 'New Accounts',
    description:
      "New accounts by when they start, who owns them, and what's needed (behind / recent starts / starting soon).",
    roles: 'all',
    size: 'full',
    defaultVisible: true,
    domains: [],
    selfFetching: true,
  },
  {
    key: 'portfolio_kpis',
    name: 'Portfolio KPIs',
    description: 'Headline scan — total ARR, weighted margin, active sites, states served.',
    roles: 'all',
    size: 'full',
    defaultVisible: true,
    domains: ['facilities', 'accounts', 'assignments'],
  },
  {
    key: 'margin_distribution',
    name: 'Margin distribution',
    description: 'Histogram of labor margin across active facilities.',
    roles: 'all',
    size: 'medium',
    defaultVisible: true,
    domains: ['facilities', 'accounts', 'assignments'],
  },
  {
    key: 'margin_extremes',
    name: 'Margin extremes',
    description: 'Top 5 and bottom 5 margin facilities (flight-risk review).',
    roles: 'all',
    size: 'medium',
    defaultVisible: true,
    domains: ['facilities', 'accounts', 'assignments'],
  },
  {
    key: 'facility_health',
    name: 'Facility health',
    description: 'Green / Amber / Red RAG buckets across all facilities.',
    roles: 'all',
    size: 'small',
    defaultVisible: true,
    domains: ['facilities'],
  },
  {
    key: 'revenue',
    name: 'Revenue',
    description: 'Current MRR/ARR and MRR added by month.',
    roles: 'all',
    size: 'small',
    defaultVisible: true,
    domains: ['facilities'],
  },
  {
    key: 'alerts',
    name: 'Operational alerts',
    description: 'Red/amber sites, open feedback, hiring, and data-quality flags.',
    roles: 'all',
    size: 'small',
    defaultVisible: true,
    domains: ['facilities', 'feedback', 'hiring'],
  },
  {
    key: 'account_health',
    name: 'Account health',
    description: 'Account status counts plus the most recent open issues.',
    roles: 'all',
    size: 'small',
    defaultVisible: true,
    domains: ['facilities', 'accounts', 'feedback'],
  },
  {
    key: 'pipeline',
    name: 'Sales pipeline',
    description: 'Opportunities grouped by stage and stage detail.',
    roles: 'all',
    size: 'small',
    defaultVisible: true,
    domains: ['opportunities'],
  },
  {
    key: 'geographic',
    name: 'Geography',
    description: 'Active facilities and MRR by market region.',
    roles: 'all',
    size: 'small',
    defaultVisible: true,
    domains: ['facilities'],
  },
  // Phase 2 parameterized widget — defaultVisible:false so the default layout
  // stays exactly today's dashboard; users opt in via the tailor panel.
  {
    key: 'revenue_trend',
    name: 'Revenue trend',
    description:
      'MRR-added trajectory with a period selector — last month, last quarter, or last 12 months.',
    roles: 'all',
    size: 'medium',
    defaultVisible: false,
    domains: ['facilities'],
  },
];

export const WIDGET_BY_KEY: ReadonlyMap<string, WidgetDef> = new Map(
  WIDGET_REGISTRY.map((d) => [d.key, d]),
);

// ----- Helpers ---------------------------------------------------------------

export function isWidgetAllowed(def: WidgetDef, role: string | null): boolean {
  return def.roles === 'all' || (role !== null && def.roles.has(role));
}

/** Catalog of widgets this role may ever see, in default order. */
export function widgetsForRole(role: string | null): WidgetDef[] {
  return WIDGET_REGISTRY.filter((d) => isWidgetAllowed(d, role));
}

/** No-prefs layout: exactly today's dashboard for this role. */
export function defaultLayoutForRole(role: string | null): LayoutEntry[] {
  return widgetsForRole(role).map((d) => ({
    widget_key: d.key,
    visible: d.defaultVisible,
  }));
}

/**
 * Reconcile a stored prefs payload (untrusted JSONB) against the registry +
 * the viewer's role:
 *   * unknown / duplicate / role-forbidden keys are dropped — a widget never
 *     renders for a role outside its gate regardless of what the JSON says;
 *   * registry widgets missing from the stored layout (e.g. added after the
 *     user saved) are appended at the end with their default visibility.
 */
export function resolveLayout(
  stored: unknown,
  role: string | null,
): LayoutEntry[] {
  const allowed = widgetsForRole(role);
  const allowedKeys = new Set(allowed.map((d) => d.key));
  const out: LayoutEntry[] = [];
  const seen = new Set<string>();
  if (Array.isArray(stored)) {
    for (const raw of stored) {
      if (raw === null || typeof raw !== 'object') continue;
      const rec = raw as Record<string, unknown>;
      const key = rec.widget_key;
      if (typeof key !== 'string' || !allowedKeys.has(key) || seen.has(key)) {
        continue;
      }
      out.push({ widget_key: key, visible: rec.visible === true });
      seen.add(key);
    }
  }
  for (const d of allowed) {
    if (!seen.has(d.key)) {
      out.push({ widget_key: d.key, visible: d.defaultVisible });
    }
  }
  return out;
}
